import React, { useState, useEffect } from "react";
import { getJoinInfo, updateJoinInfo, uploadFile, getUploadUrl } from "../api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Save, Upload, FileText, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

export const AdminJoinInfo = () => {
  const [joinInfo, setJoinInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getJoinInfo();
        setJoinInfo(data);
      } catch (error) {
        console.error("Error fetching join info:", error);
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateJoinInfo({
        text: joinInfo.text,
        downloadLabel: joinInfo.downloadLabel,
        downloadUrl: joinInfo.downloadUrl,
        contactEmail: joinInfo.contactEmail,
      });
      toast.success("Page 'Nous rejoindre' mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const result = await uploadFile(file);
      const fileUrl = getUploadUrl(result.url);
      setJoinInfo((prev) => ({ ...prev, downloadUrl: fileUrl }));
      toast.success("Fichier uploadé avec succès");
    } catch (error) {
      toast.error("Erreur lors de l'upload du fichier");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!joinInfo) return null;

  return (
    <div className="space-y-6">
      {/* Join Text */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">
            Texte de la page "Nous rejoindre"
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={joinInfo.text}
            onChange={(e) =>
              setJoinInfo((prev) => ({ ...prev, text: e.target.value }))
            }
            rows={10}
            className="resize-y font-normal text-sm"
            placeholder="Texte d'invitation à rejoindre le syndicat..."
          />
          <p className="text-xs text-slate-400">
            Utilisez • pour les puces et séparez les paragraphes par une ligne vide.
          </p>
        </CardContent>
      </Card>

      {/* Download File */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-sky-500" />
            Fichier téléchargeable
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Libellé du bouton</Label>
            <Input
              value={joinInfo.downloadLabel}
              onChange={(e) =>
                setJoinInfo((prev) => ({
                  ...prev,
                  downloadLabel: e.target.value,
                }))
              }
              placeholder="Télécharger le bulletin d'adhésion"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-500">Fichier actuel</Label>
            <div className="flex items-center gap-3 mt-1.5">
              <div className="flex-1">
                <Input
                  value={joinInfo.downloadUrl}
                  onChange={(e) =>
                    setJoinInfo((prev) => ({
                      ...prev,
                      downloadUrl: e.target.value,
                    }))
                  }
                  placeholder="URL du fichier ou uploadez un fichier"
                  className="text-sm"
                />
              </div>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors cursor-pointer flex-shrink-0">
                <Upload className="w-4 h-4" />
                Uploader
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.png"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleFileUpload(e.target.files[0]);
                    }
                  }}
                />
              </label>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Formats acceptés : PDF, DOC, DOCX, JPG, PNG
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Contact Email */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">
            Email de contact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="email"
            value={joinInfo.contactEmail}
            onChange={(e) =>
              setJoinInfo((prev) => ({
                ...prev,
                contactEmail: e.target.value,
              }))
            }
            placeholder="contact@unsa-aegide-domitys.fr"
          />
        </CardContent>
      </Card>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-500 text-white text-sm font-semibold rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
      >
        <Save className="w-4 h-4" />
        {saving ? "Sauvegarde..." : "Sauvegarder toutes les modifications"}
      </button>
    </div>
  );
};
